import { useState, useEffect } from 'react';
import { propertyApi, ApiRequestError, type CalendarDayDisplay } from '@/lib/api';
import RewardTiersModal from '@/components/RewardTiersModal';

export default function SettingsPage() {
  const [settings, setSettings] = useState({
    property_name: '',
    timezone: 'America/New_York',
    default_checkin_time: '12:00',
    default_checkout_time: '12:00',
    calendar_day_display: 'half-bar' as CalendarDayDisplay,
    welcome_email_bullets: '',
    checkout_email_bullets: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [isRewardsModalOpen, setIsRewardsModalOpen] = useState(false);

  // Email preview state
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    type: 'welcome' | 'checkout' | null;
    subject: string;
    html: string;
    isLoading: boolean;
  }>({
    isOpen: false,
    type: null,
    subject: '',
    html: '',
    isLoading: false,
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { property } = await propertyApi.get();
        setSettings({
          property_name: property.property_name,
          timezone: property.timezone,
          default_checkin_time: property.default_checkin_time,
          default_checkout_time: property.default_checkout_time,
          calendar_day_display: property.calendar_day_display || 'half-bar',
          welcome_email_bullets: property.welcome_email_bullets || '',
          checkout_email_bullets: property.checkout_email_bullets || '',
        });
      } catch (err) {
        if (err instanceof ApiRequestError) {
          setError(err.message);
        } else {
          setError('Failed to load settings');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async () => {
    setError(null);
    setSuccess(false);
    setIsSaving(true);

    try {
      await propertyApi.update(settings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to save settings');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = async (type: 'welcome' | 'checkout') => {
    setPreviewModal({
      isOpen: true,
      type,
      subject: '',
      html: '',
      isLoading: true,
    });

    try {
      const result = await propertyApi.emailPreview({
        type,
        welcome_email_bullets: settings.welcome_email_bullets,
        checkout_email_bullets: settings.checkout_email_bullets,
      });

      setPreviewModal((prev) => ({
        ...prev,
        subject: result.subject,
        html: result.html,
        isLoading: false,
      }));
    } catch (err) {
      setPreviewModal((prev) => ({
        ...prev,
        isLoading: false,
        html: '<p style="color: red;">Failed to load preview</p>',
      }));
    }
  };

  const closePreview = () => {
    setPreviewModal({
      isOpen: false,
      type: null,
      subject: '',
      html: '',
      isLoading: false,
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Settings</h1>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
          Settings saved successfully!
        </div>
      )}

      <div className="card p-6 space-y-6">
        <h2 className="text-lg font-semibold text-gray-900">
          Property Settings
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="property-name" className="label">
              Property Name
            </label>
            <input
              id="property-name"
              type="text"
              value={settings.property_name}
              onChange={(e) =>
                setSettings({ ...settings, property_name: e.target.value })
              }
              className="input"
            />
          </div>

          <div>
            <label htmlFor="timezone" className="label">
              Timezone
            </label>
            <select
              id="timezone"
              value={settings.timezone}
              onChange={(e) =>
                setSettings({ ...settings, timezone: e.target.value })
              }
              className="input"
            >
              <option value="America/New_York">Eastern Time (New York)</option>
              <option value="America/Chicago">Central Time (Chicago)</option>
              <option value="America/Denver">Mountain Time (Denver)</option>
              <option value="America/Los_Angeles">Pacific Time (Los Angeles)</option>
              <option value="Europe/London">London</option>
              <option value="Europe/Paris">Paris</option>
              <option value="Asia/Tokyo">Tokyo</option>
            </select>
          </div>

          <div>
            <label htmlFor="checkin-time" className="label">
              Default Arrival Time
            </label>
            <input
              id="checkin-time"
              type="time"
              value={settings.default_checkin_time}
              onChange={(e) =>
                setSettings({ ...settings, default_checkin_time: e.target.value })
              }
              className="input"
            />
          </div>

          <div>
            <label htmlFor="checkout-time" className="label">
              Default Departure Time
            </label>
            <input
              id="checkout-time"
              type="time"
              value={settings.default_checkout_time}
              onChange={(e) =>
                setSettings({ ...settings, default_checkout_time: e.target.value })
              }
              className="input"
            />
          </div>

          <div className="md:col-span-2">
            <label className="label">Calendar Day Display</label>
            <p className="text-sm text-gray-500 mb-3">
              Choose how arrival and departure days are displayed on the calendar
            </p>
            <div className="flex gap-4">
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 flex-1">
                <input
                  type="radio"
                  name="calendar_day_display"
                  value="half-bar"
                  checked={settings.calendar_day_display === 'half-bar'}
                  onChange={(e) =>
                    setSettings({ ...settings, calendar_day_display: e.target.value as CalendarDayDisplay })
                  }
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-gray-900">Half Bar</div>
                  <div className="text-sm text-gray-500">
                    Arrival days show bar on right half, departure days on left half
                  </div>
                  <div className="mt-2 flex items-center gap-1">
                    <div className="w-8 h-4 bg-gray-200 rounded relative overflow-hidden">
                      <div className="absolute right-0 top-0 bottom-0 w-1/2 bg-primary-500 rounded-l"></div>
                    </div>
                    <span className="text-xs text-gray-400">Arrival</span>
                    <div className="w-8 h-4 bg-gray-200 rounded relative overflow-hidden ml-2">
                      <div className="absolute left-0 top-0 bottom-0 w-1/2 bg-primary-500 rounded-r"></div>
                    </div>
                    <span className="text-xs text-gray-400">Departure</span>
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 flex-1">
                <input
                  type="radio"
                  name="calendar_day_display"
                  value="half-box"
                  checked={settings.calendar_day_display === 'half-box'}
                  onChange={(e) =>
                    setSettings({ ...settings, calendar_day_display: e.target.value as CalendarDayDisplay })
                  }
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-gray-900">Half Box</div>
                  <div className="text-sm text-gray-500">
                    Arrival days show bottom half filled, departure days show top half filled
                  </div>
                  <div className="mt-2 flex items-center gap-1">
                    <div className="w-8 h-4 bg-gray-200 rounded relative overflow-hidden">
                      <div className="absolute left-0 right-0 bottom-0 h-1/2 bg-primary-500"></div>
                    </div>
                    <span className="text-xs text-gray-400">Arrival</span>
                    <div className="w-8 h-4 bg-gray-200 rounded relative overflow-hidden ml-2">
                      <div className="absolute left-0 right-0 top-0 h-1/2 bg-primary-500"></div>
                    </div>
                    <span className="text-xs text-gray-400">Departure</span>
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="btn-primary"
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      <div className="card p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Rewards Tiers</h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage lifetime tiers and annual badge tiers, including uploading badge images.
            </p>
          </div>
          <button
            onClick={() => setIsRewardsModalOpen(true)}
            className="btn-primary"
            type="button"
          >
            Manage Tiers
          </button>
        </div>
      </div>

      <div className="card p-6 space-y-6">
        <h2 className="text-lg font-semibold text-gray-900">
          Email Reminder Settings
        </h2>
        <p className="text-sm text-gray-600">
          Reminder emails are automatically sent 3 days and 1 day before arrival, and 1 day before departure.
        </p>
        <p className="text-sm text-gray-600">
          Customize the bullet points that appear in arrival and departure reminder emails.
          Enter one item per line.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="welcome-bullets" className="label mb-0">
                Pre-Arrival Email Bullets
              </label>
              <button
                onClick={() => handlePreview('welcome')}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Preview Email
              </button>
            </div>
            <textarea
              id="welcome-bullets"
              value={settings.welcome_email_bullets}
              onChange={(e) =>
                setSettings({ ...settings, welcome_email_bullets: e.target.value })
              }
              className="input min-h-[150px]"
              placeholder="Arrival time is at noon&#10;Keys and access code will be provided&#10;Review the house rules"
              rows={6}
            />
            <p className="text-xs text-gray-500 mt-1">
              Shown in the "Before You Arrive" section of reminder emails
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="checkout-bullets" className="label mb-0">
                Departure Reminder Bullets
              </label>
              <button
                onClick={() => handlePreview('checkout')}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Preview Email
              </button>
            </div>
            <textarea
              id="checkout-bullets"
              value={settings.checkout_email_bullets}
              onChange={(e) =>
                setSettings({ ...settings, checkout_email_bullets: e.target.value })
              }
              className="input min-h-[150px]"
              placeholder="Departure time is at noon&#10;Strip beds and start laundry&#10;Take out trash&#10;Lock all doors and windows"
              rows={6}
            />
            <p className="text-xs text-gray-500 mt-1">
              Shown in the "Before You Leave" section of departure emails
            </p>
          </div>
        </div>

        <div className="pt-4 border-t flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="btn-primary"
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Email Preview Modal */}
      {previewModal.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={closePreview}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {previewModal.type === 'welcome'
                      ? 'Pre-Arrival Email Preview'
                      : 'Departure Reminder Email Preview'}
                  </h3>
                  {previewModal.subject && (
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Subject:</span> {previewModal.subject}
                    </p>
                  )}
                </div>
                <button
                  onClick={closePreview}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-auto p-4">
                {previewModal.isLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <iframe
                      srcDoc={previewModal.html}
                      className="w-full h-[600px] bg-white"
                      title="Email Preview"
                    />
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
                <button onClick={closePreview} className="btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <RewardTiersModal
        isOpen={isRewardsModalOpen}
        onClose={() => setIsRewardsModalOpen(false)}
      />
    </div>
  );
}
